import { saveFieldDetails } from '../../actions/odooFieldActions';
import { markFieldDetailsDirty, updateFieldDetails } from '../../actions/odooFormActions';
import { areFieldValuesEqual } from '../../utils/fieldUtils';
import AddressfinderField from './formFields/AddressfinderField';
import CheckboxField from './formFields/CheckboxField';
import CountryField from './formFields/CountryField';
import DateField from './formFields/DateField';
import FileUploadField from './formFields/FileUploadField';
import HTMLDisplay from './formFields/HTMLDisplay';
import HiddenField from './formFields/HiddenField';
import RadioField from './formFields/RadioField';
import SelectField from './formFields/SelectField';
import SelectMultiField from './formFields/SelectMultiField';
import TextField from './formFields/TextField';
import TextareaField from './formFields/TextareaField';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Field, useFormState } from 'react-final-form';
import { connect } from 'react-redux';

const componentMap = {
  text: TextField,
  address: AddressfinderField,
  email: TextField,
  float: TextField,
  hidden: HiddenField,
  radio: RadioField,
  select: SelectField,
  select_multi: SelectMultiField,
  checkbox: CheckboxField,
  textarea: TextareaField,
  country: CountryField,
  country_multi: CountryField,
  date: DateField,
  file_upload: FileUploadField,
  html_display: HTMLDisplay,
};

const composeValidators =
  (...validators) =>
  (value, allValues, meta) =>
    validators.reduce((error, validator) => error || validator(value, allValues, meta), undefined);

// Validates that a value has been set
const requiredValidation = (value) => {
  if (value === undefined || value === null || value === '' || value.length === 0) {
    return 'This field is required';
  } else {
    return undefined;
  }
};

// Validates that file uploads do not exceed the maxFileSize
const maxFileSizeValidation = (maxFileSize) => (value) => {
  if (!value) {
    return undefined;
  }

  let uploadSize = 0;
  value.forEach((file) => {
    uploadSize += file.fileSize;
  });

  if (uploadSize < maxFileSize) {
    return undefined;
  } else {
    return `The total size of all selected files is too large. Please select up to ${prettyFileSize(maxFileSize)}`;
  }
};

// Validates that file uploads do not exceed a number of files (maxUploads)
const maxUploadValidation = (maxUploads) => (value) => {
  if (!value) {
    return undefined;
  }

  if (value.length <= maxUploads) {
    return undefined;
  } else {
    return `Too many files have been selected. Please select up to ${maxUploads} files.`;
  }
};

// Converts a size in bytes to a string with units
const prettyFileSize = (size) => {
  if (size > 999999) {
    return Math.round(size / 1000000) + ' MB';
  } else if (size > 999) {
    return Math.round(size / 1000) + ' kB';
  } else {
    return size + ' bytes';
  }
};

// Validates that select multi does not have too many selected options, limited to maxSelected
const maxSelectedValidation = (maxSelected) => (value) => {
  if (!value) {
    return undefined;
  }

  if (value.length <= maxSelected) {
    return undefined;
  } else {
    return `Too many options have been selected. Please select up to ${maxSelected} options.`;
  }
};

// Validates that a string is a valid date
const dateValidation = (date) => {
  if (date === undefined) return undefined; // Date has not been set
  if (date === 'invalid-date') {
    return 'Please enter a valid date';
  }
};

// Find a form value for a given fieldname and an optional has-many fieldgroup row
const findFieldValue = (fieldName, hasManyRow, formValues, parent) => {
  if (typeof formValues === 'undefined') {
    return null;
  }

  if (typeof formValues[parent.name] !== 'undefined') {
    if (typeof formValues[parent.name][hasManyRow] !== 'undefined') {
      if (typeof formValues[parent.name][hasManyRow][fieldName] !== 'undefined') {
        return formValues[parent.name][hasManyRow][fieldName];
      }
    }
  }

  // Look directly in formValues for fields that aren't in a hasMany fieldgroup
  if (typeof formValues[fieldName] !== 'undefined') {
    return formValues[fieldName];
  }

  // Check if the source and target fields are in the same hasMany fieldgroup
  if (typeof hasManyRow !== 'undefined' && hasManyRow !== null && parent.hasMany === true) {
    // Look in formValues for this field using parent hasMany fieldGroup name
    if (
      typeof formValues[parent.name] !== 'undefined' &&
      typeof formValues[parent.name][hasManyRow] !== 'undefined' &&
      typeof formValues[parent.name][hasManyRow][fieldName] !== 'undefined'
    ) {
      return formValues[parent.name][hasManyRow][fieldName];
    }
  }

  // Look in ALL hasMany groups for this field
  let values = [];
  for (let i in formValues) {
    if (!(formValues[i] instanceof Array)) {
      continue;
    }
    // We don't want values from the same fieldgroup as this field - we checked
    // for that above and found nothing already.
    if (parent.name === i) {
      continue;
    }
    // Make a list of all matching fieldName values and return it
    for (let j = 0, jj = formValues[i].length; j < jj; j++) {
      if (typeof formValues[i][j][fieldName] !== 'undefined') {
        // We found a hasMany group that contains this fieldName
        values.push(formValues[i][j][fieldName]);
      }
    }
  }
  // If we found any values in hasMany groups, return them
  if (values.length > 0) {
    return values;
  }

  // We didn't find a value for this fieldName.
  return null;
};

const FormField = (props) => {
  const { data, parent, hasManyRow, breadCrumbs, saveFieldDetails, markFieldDetailsDirty, updateFieldDetails, skipRequiredTest } = props;
  const { values: formValues } = useFormState();
  const {
    fieldName,
    fieldDetails,
    hasManyFieldDetails,
    renderType,
    required,
    value,
    helpText,
    maxFileSize,
    maxUploads,
    maxSelected,
    isUpdating,
    disableExpression,
    hideExpression,
    showExpression,
    fieldCssClass,
    displayIndent,
    dependantFields,
    label,
    disabled: fieldDisabled,
    placeholder: rawPlaceholder,
  } = data;

  const Component = componentMap[renderType] || TextField;
  const type = ['email', 'float', 'text', 'address'].includes(renderType) ? 'text' : undefined;
  let multi = renderType.includes('multi');
  if ((renderType === 'select_multi' || renderType === 'country_multi') && typeof maxSelected !== 'undefined') {
    multi = maxSelected > 1;
  }
  const autocomplete = renderType === 'address' ? false : true;

  const validators = useMemo(() => {
    const result = [];
    if (required && !skipRequiredTest) result.push(requiredValidation);
    if (renderType === 'date') result.push(dateValidation);
    if (renderType === 'file_upload') {
      if (maxFileSize) result.push(maxFileSizeValidation(maxFileSize));
      if (maxUploads) result.push(maxUploadValidation(maxUploads));
    }
    if ((renderType === 'select_multi' || renderType === 'country_multi') && maxSelected) {
      result.push(maxSelectedValidation(maxSelected));
    }
    return result;
  }, [required, skipRequiredTest, renderType, maxFileSize, maxUploads, maxSelected]);

  // Normalize isUpdating for hasMany rows
  const isUpdatingValue = useMemo(() => {
    return Array.isArray(isUpdating) ? !!isUpdating[hasManyRow] : !!isUpdating;
  }, [isUpdating, hasManyRow]);

  const dependencyValues = useMemo(() => {
    if (!fieldDetails?.dependsOn || !Array.isArray(fieldDetails.dependsOn)) {
      return null;
    }
    return fieldDetails.dependsOn.map((dependency) => {
      if (dependency.isHasMany && hasManyRow != null) {
        return formValues?.[parent.name]?.[hasManyRow]?.[dependency.fieldName];
      }
      return formValues?.[dependency.fieldName];
    });
  }, [fieldDetails?.dependsOn, formValues, hasManyRow, parent.name]);

  const lastFetchedDependencySignatureRef = useRef(null);

  useEffect(() => {
    if (!fieldDetails?.dependsOn || !Array.isArray(fieldDetails.dependsOn) || !dependencyValues) {
      return;
    }
    // Avoid re-entrant updates while this field is updating
    if (isUpdatingValue) {
      return;
    }

    for (let i = 0; i < dependencyValues.length; i++) {
      const value = dependencyValues[i];
      if (value == null || (Array.isArray(value) && value.length === 0) || value === '') {
        lastFetchedDependencySignatureRef.current = null;
        return;
      }
    }

    // Only refetch when dependsOn values actually change. isUpdating toggling alone
    // must not re-trigger a fetch (that caused an infinite lookup loop).
    const dependencySignature = JSON.stringify(dependencyValues);
    if (lastFetchedDependencySignatureRef.current === dependencySignature) {
      return;
    }

    lastFetchedDependencySignatureRef.current = dependencySignature;
    updateFieldDetails(
      fieldName.replace(/(.*)\]\./, ''),
      parent.name,
      hasManyRow,
      fieldDetails.dependsOn,
      fieldDetails.endpoint,
      formValues,
    );
  }, [
    dependencyValues,
    fieldDetails?.dependsOn,
    fieldDetails?.endpoint,
    fieldName,
    formValues,
    hasManyRow,
    isUpdatingValue,
    parent.name,
    updateFieldDetails,
  ]);

  const onChangeHandler = (currentValue) => {
    saveFieldDetails(fieldName.replace(/(.*)\]\./, ''), parent.name, hasManyRow, currentValue);

    if (!dependantFields) return;

    const sourceIsHasMany = hasManyRow !== null;
    dependantFields.forEach((f) => {
      if (sourceIsHasMany && f.isHasMany && parent.name === f.fieldGroupName) {
        markFieldDetailsDirty(f.fieldName, f.fieldGroupName, hasManyRow);
      } else if (f.isHasMany) {
        const count = formValues?.[f.fieldGroupName]?.length || 0;
        for (let i = 0; i < count; i++) {
          markFieldDetailsDirty(f.fieldName, f.fieldGroupName, i);
        }
      } else {
        markFieldDetailsDirty(f.fieldName, f.fieldGroupName, null);
      }
    });
  };

  const compareFieldValue = useCallback(
    (fieldName, row, compareValue) => {
      const val = findFieldValue(fieldName, row, formValues, parent);
      return areFieldValuesEqual(val, compareValue);
    },
    [formValues, parent],
  );

  const shouldHide = () => {
    if (!hideExpression) return false;
    try {
      const rules = JSON.parse(hideExpression);
      const result = rules.some((r) => {
        const cmp = compareFieldValue(r.fieldName, hasManyRow, r.value);
        return cmp;
      });
      return result;
    } catch {
      return false;
    }
  };

  const shouldShow = () => {
    if (!showExpression) return true;
    try {
      const rules = JSON.parse(showExpression);
      const result = rules.some((r) => {
        const cmp = compareFieldValue(r.fieldName, hasManyRow, r.value);
        return cmp;
      });
      return result;
    } catch {
      return true;
    }
  };

  const isDisabled = useMemo(() => {
    if (fieldDisabled) return true;
    if (!disableExpression) return false;
    try {
      const rules = JSON.parse(disableExpression);
      return rules.some((r) => compareFieldValue(r.fieldName, hasManyRow, r.value));
    } catch {
      return false;
    }
  }, [fieldDisabled, disableExpression, hasManyRow, compareFieldValue]);

  const placeholder = isUpdatingValue ? 'Loading...' : rawPlaceholder;

  const options = useMemo(() => {
    return hasManyRow !== null && hasManyFieldDetails?.[hasManyRow] ? hasManyFieldDetails[hasManyRow] : fieldDetails;
  }, [hasManyRow, hasManyFieldDetails, fieldDetails]);

  const cssClass = [fieldCssClass, displayIndent ? `indent-${parseInt(displayIndent, 10)}` : 'no-indent'].filter(Boolean).join(' ');

  if (Component === HTMLDisplay) {
    return <HTMLDisplay value={value} cssClass={cssClass} breadCrumbs={breadCrumbs} />;
  }

  if (shouldHide() || !shouldShow()) return null;

  return (
    <Field name={fieldName} type={type} validate={composeValidators(...validators)}>
      {({ input, meta }) => {
        return (
          <Component
            {...input}
            input={input}
            label={label}
            placeholder={placeholder}
            options={options}
            maxUploads={maxUploads}
            multi={multi}
            helpText={helpText}
            cssClass={cssClass}
            disabled={isDisabled}
            labelRequired={required}
            parent={parent}
            hasManyRow={hasManyRow}
            autocomplete={autocomplete}
            meta={meta}
            error={meta.touched && meta.error}
            onChange={(eventOrValue) => {
              const newValue = eventOrValue?.target?.value ?? eventOrValue;
              onChangeHandler(newValue);
              input.onChange(eventOrValue);
            }}
          />
        );
      }}
    </Field>
  );
};

const mapStateToProps = (state) => ({
  formValues: state.form?.odooForm?.values || {},
});

const mapDispatchToProps = {
  markFieldDetailsDirty,
  updateFieldDetails,
  saveFieldDetails,
};

export default connect(mapStateToProps, mapDispatchToProps)(FormField);
